import React, { useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { selectIsArchivedLicense, selectLicenseDetails } from '../duck/selectors/license';
import { State } from 'model/State';
import config from 'appconfig';
import {
  StyledButtonsWrapper,
  StyledFlex,
  StyledLink,
  StyledTableData,
  StyledTextLabel,
  TextAreaWrapper,
} from './licensesExpandedRow/StyledComponents';
import TextArea from '@atlaskit/textarea';
import styled from 'styled-components';
import { DetailsRow } from './licensesExpandedRow/DetailsRowContentElements/DetailsRow';
import { FormattedMessage } from 'react-intl';
import { messages } from '../messages';
import {
  addOnProductTypes,
  serverIdNotRequiredForKeys,
  serverIdRequiredForProductTypes,
} from 'util/mac/helpers';
import { StyledPipe } from './licensesExpandedRow/ActionsSectionElements/StyledPipe';
import { ActionsMessage } from './licensesExpandedRow/ActionsSectionElements/ActionsMessage';
import { fetchGenericProperty } from '../duck/actions/genericProperty';
import {
  getMACUserChannel,
  selectGenericPropertyByFullKey,
  selectHasSourceDownloads,
} from '../duck/selectors';
import { fetchHasSourceDownloads } from '../duck/actions/downloads';
import { DownloadSourceLink } from './licensesExpandedRow/ActionsSectionElements/DownloadSourceLink';
import { ArchiveUnarchiveButtonLink } from './licensesExpandedRow/ActionsSectionElements/ArchiveUnarchiveButtonLink';
import Spinner from '@atlaskit/spinner';
import { RESET_ARCHIVE_UNARCHIVE_LICENSE } from '../duck/actions/license';
import TrialLicenseConfirmationModal from './licensesExpandedRow/TrialLicenseConfirmationModal';

const SpinnerWrapper = styled.div`
  width: 100%;
  display: flex;
  justify-content: center;
`;

const TrialLicenseExpandedRow = ({ licenseId, isArchived }) => {
  const dispatch = useDispatch();

  const [genericPropertyFullKey, setGenericPropertyFullKey] = useState<string>('');
  const [keysList, setKeysList] = useState<string[]>([]);
  const [showSourceDownloadBtn, setShowSourceDownloadBtn] = useState(false);

  const { isPartner } = useSelector((state: State) => getMACUserChannel(state));
  const licenseDetails = useSelector((state) => selectLicenseDetails(state as State, licenseId));
  const genericPropertyTextValue = useSelector((state) =>
    selectGenericPropertyByFullKey(state as State, genericPropertyFullKey),
  );
  const hasSourceDownloads = useSelector(selectHasSourceDownloads);
  const isLicenseSuccessfullyArchived = useSelector(selectIsArchivedLicense);

  const details = licenseDetails?.licenseDetails;
  const packageDetails = licenseDetails?.productPackageDetails;

  const shouldShowViewLicenseLink =
    !isPartner &&
    !details?.sid &&
    (details?.description?.includes('Evaluation') || details?.description?.includes('Personal')) &&
    serverIdRequiredForProductTypes.includes(packageDetails?.productType) &&
    !serverIdNotRequiredForKeys.includes(packageDetails?.key);

  const displayBuyLink = packageDetails?.key && packageDetails?.publiclyAvailable;
  const productKey = packageDetails?.key?.replace('pricingplan.', '');

  const downloadLink = () => {
    let link: string = '';
    if (
      serverIdRequiredForProductTypes.includes(packageDetails?.productType) &&
      packageDetails?.downloadProduct?.downloadUrl
    ) {
      link = packageDetails?.downloadProduct?.downloadUrl;
    } else if (addOnProductTypes.includes(packageDetails?.productType)) {
      const key = packageDetails.key.replace('pricingplan.', '');
      link = `${config.marketplaceUrl}/plugins/${key}`;
    }
    return link;
  };

  const downloadText = () => {
    let text: React.ReactNode;
    if (
      serverIdRequiredForProductTypes.includes(packageDetails?.productType) &&
      packageDetails?.downloadProduct?.downloadUrl
    ) {
      text = (
        <FormattedMessage
          {...messages.downloadWithDescription}
          values={{ description: packageDetails?.description }}
        />
      );
    } else if (addOnProductTypes.includes(packageDetails?.productType)) {
      text = <FormattedMessage id="mac.licenses.download" defaultMessage="Download" />;
    }
    return text;
  };

  useEffect(() => {
    if (packageDetails?.downloadProduct?.key && details?.licenseType === 'DEVELOPER') {
      setGenericPropertyFullKey(`related.source.product.${packageDetails.downloadProduct.key}`);
      dispatch(fetchGenericProperty(genericPropertyFullKey));
    }
  }, [packageDetails, genericPropertyFullKey]);

  useEffect(() => {
    if (genericPropertyTextValue?.length && details?.licenseType === 'DEVELOPER') {
      const keys: string[] = [...genericPropertyTextValue.map(({ textValue }) => textValue)];
      setKeysList(keys);

      genericPropertyTextValue?.forEach((genericPropertyItem) => {
        dispatch(fetchHasSourceDownloads(genericPropertyItem.textValue));
      });
    } else if (
      packageDetails?.downloadProduct?.key &&
      !hasSourceDownloads?.[packageDetails.downloadProduct.key]?.inProgress
    ) {
      setKeysList([packageDetails.downloadProduct.key]);
      dispatch(fetchHasSourceDownloads(packageDetails.downloadProduct.key));
    }
  }, [genericPropertyTextValue, packageDetails?.downloadProduct?.key]);

  useEffect(() => {
    if (hasSourceDownloads && keysList.length && details?.licenseType === 'DEVELOPER') {
      keysList.forEach((key) => {
        if (hasSourceDownloads[key] && hasSourceDownloads[key]._result === true) {
          setShowSourceDownloadBtn(true);
        }
      });
    }
  }, [hasSourceDownloads, keysList.length]);

  useEffect(() => {
    if (isLicenseSuccessfullyArchived === '') {
      window.location.href = `${config.root}/my`;
      dispatch({
        type: RESET_ARCHIVE_UNARCHIVE_LICENSE,
      });
    }
  }, [isLicenseSuccessfullyArchived]);

  return (
    <>
      {details ? (
        <>
          <TrialLicenseConfirmationModal licenseKey={details?.license} sen={details?.id} />
          <StyledTableData colSpan={3}>
            {details?.sid && (
              <DetailsRow
                label={<FormattedMessage {...messages.serverIdLabel} />}
                value={details?.sid}
              />
            )}
            <DetailsRow
              label={<FormattedMessage {...messages.SEN} />}
              value={details?.id ? `SEN-${details?.id}` : ''}
            />
          </StyledTableData>
          <StyledTableData colSpan={2}>
            <StyledFlex>
              <StyledTextLabel>
                <FormattedMessage id="mac.licenses.license.key" defaultMessage="License key" />
              </StyledTextLabel>
              {shouldShowViewLicenseLink ? (
                <StyledLink href={`/purchase/my/license/license/${details?.id}`}>
                  <FormattedMessage id="mac.licenses.view.license" defaultMessage="View license" />
                </StyledLink>
              ) : (
                <TextAreaWrapper>
                  <TextArea
                    id="area"
                    maxHeight="20vh"
                    value={details?.license}
                    readOnly
                    onClick={(e) => {
                      e.currentTarget.select();
                    }}
                  />
                </TextAreaWrapper>
              )}
            </StyledFlex>
            <StyledFlex>
              <ActionsMessage />
              <StyledButtonsWrapper>
                {details?.licenseType === 'DEVELOPER' && (
                  <>
                    <>
                      <StyledLink href={downloadLink()}>{downloadText()}</StyledLink>
                      <StyledPipe />
                    </>
                    {showSourceDownloadBtn && (
                      <DownloadSourceLink productKey={packageDetails?.downloadProduct?.key} />
                    )}
                  </>
                )}
                {details?.evaluation && (
                  <>
                    {displayBuyLink && (
                      <>
                        <StyledLink href={`/purchase/product/${productKey}`}>
                          <FormattedMessage {...messages.buy} />
                        </StyledLink>
                        <StyledPipe />
                      </>
                    )}
                    {downloadLink() && downloadText() && (
                      <>
                        <StyledLink href={downloadLink()}>{downloadText()}</StyledLink>
                        <StyledPipe />
                      </>
                    )}
                    {packageDetails?.marketplaceAddOn && (
                      <>
                        <StyledLink href={`/purchase/my/addon/try/${packageDetails?.key}`}>
                          <FormattedMessage {...messages.licenseEvaluationPageTitle} />
                        </StyledLink>
                        <StyledPipe />
                      </>
                    )}
                  </>
                )}
                <ArchiveUnarchiveButtonLink
                  isArchiveButtonVisible
                  isArchived={isArchived}
                  accountId={licenseId}
                  isAccount={false}
                />
              </StyledButtonsWrapper>
            </StyledFlex>
          </StyledTableData>
        </>
      ) : (
        <StyledTableData colSpan={5}>
          <SpinnerWrapper>
            <Spinner />
          </SpinnerWrapper>
        </StyledTableData>
      )}
    </>
  );
};

export default TrialLicenseExpandedRow;
